/**
 * API service for the Learning System.
 */

import { authFetch } from './api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const fetch = authFetch;

// =============================================================================
// TYPES
// =============================================================================

export interface ProgramPart {
  name: string;
  icon: string;
  modules: Record<string, ProgramModule>;
}

export interface ProgramModule {
  name: string;
  icon: string;
  skills: Record<string, ProgramSkill>;
}

export interface ProgramSkill {
  name: string;
  lessons: LessonConfig[];
}

export interface LessonConfig {
  number: number;
  title: string;
  subtitle?: string;
}

export interface RuleContent {
  title: string;       // Titre de la règle
  rule: string;        // Règle principale (texte)
  method: string;      // Méthode/sous-règles formatées
}

export interface MnemonicTip {
  tip: string;
  example?: string;
}

export interface IllustratedExample {
  sentence: string;
  explanation: string;
  highlight_words: string[];
  is_correct: boolean;
}

export interface Exercise {
  id: string;
  type: 'fill_blank' | 'multiple_choice' | 'correction' | 'matching' | 'ordering' | 'true_false' | 'free_text';
  difficulty: 'beginner' | 'easy' | 'medium' | 'hard' | 'expert';
  question: string;  // The question text
  instruction?: string;  // Alternative field name
  options?: string[];  // For multiple choice
  content?: Record<string, any>;  // Additional content
  correct_answer: any;
  explanation: string;
  hint?: string;
  points: number;
}

export interface LessonContent {
  introduction: string;
  rule: RuleContent;
  tips: MnemonicTip[];
  examples: IllustratedExample[];
  summary: string[];
}

export interface Lesson {
  id: string;
  skill_id: string;
  number: number;
  title: string;
  subtitle?: string;
  duration_minutes: number;
  order_index: number;
  content?: LessonContent;
  exercises: Exercise[];
  created_at?: string;
  generated_by_ai: boolean;
}

export interface GenerateLessonRequest {
  part_name: string;
  module_name: string;
  skill_name: string;
  lesson_title: string;
  lesson_number: number;
  context?: string;
  num_exercises?: number;
}

export interface GenerateMoreExercisesRequest {
  lesson_id: string;
  lesson_title: string;
  rule_summary: string;
  count: number;
  difficulty: 'beginner' | 'easy' | 'medium' | 'hard' | 'expert';
  contexts?: string[];
  exclude_sentences?: string[];
}

export interface ExplainAgainRequest {
  lesson_id: string;
  lesson_title: string;
  original_rule: string;
  confusion_type: 'rule' | 'application' | 'exceptions';
  user_errors?: string[];
}

export interface ChallengeRequest {
  lesson_id: string;
  lesson_title: string;
  question_count: number;
  time_limit_seconds: number;
}

// =============================================================================
// API CALLS
// =============================================================================

/**
 * Get the full program structure.
 */
export async function getProgramStructure(): Promise<{
  success: boolean;
  program: Record<string, ProgramPart>;
  statistics: {
    total_parts: number;
    total_modules: number;
    total_skills: number;
    total_lessons: number;
  };
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/program`);
  if (!response.ok) {
    throw new Error('Failed to fetch program structure');
  }
  return response.json();
}

/**
 * List all available lessons.
 */
export async function listLessons(partId?: string): Promise<{
  success: boolean;
  count: number;
  lessons: Array<{
    part_id: string;
    part_name: string;
    module_id: string;
    module_name: string;
    skill_id: string;
    skill_name: string;
    lesson_number: number;
    lesson_title: string;
    lesson_subtitle: string;
  }>;
}> {
  const url = partId
    ? `${API_BASE_URL}/api/learning/lessons?part_id=${partId}`
    : `${API_BASE_URL}/api/learning/lessons`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch lessons');
  }
  return response.json();
}

/**
 * Generate a complete lesson with AI.
 */
export async function generateLesson(request: GenerateLessonRequest): Promise<{
  success: boolean;
  lesson?: Lesson;
  message: string;
  generation_time_seconds: number;
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/generate/lesson`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to generate lesson');
  }
  return response.json();
}

/**
 * Generate more exercises for reinforcement.
 */
export async function generateMoreExercises(request: GenerateMoreExercisesRequest): Promise<{
  success: boolean;
  exercises: Exercise[];
  count: number;
  message: string;
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/exercises/more`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to generate exercises');
  }
  return response.json();
}

/**
 * Get an alternative explanation when the user didn't understand.
 */
export async function explainAgain(request: ExplainAgainRequest): Promise<{
  success: boolean;
  alternative_rule?: RuleContent;
  new_examples: IllustratedExample[];
  extra_tips: MnemonicTip[];
  simplified_exercises: Exercise[];
  encouragement: string;
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/explain-again`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get explanation');
  }
  return response.json();
}

/**
 * Create a timed challenge.
 */
export async function createChallenge(request: ChallengeRequest): Promise<{
  success: boolean;
  challenge_id: string;
  questions: Exercise[];
  time_limit_seconds: number;
  message: string;
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/challenge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create challenge');
  }
  return response.json();
}

/**
 * Get program statistics.
 */
export async function getProgramStats(): Promise<{
  success: boolean;
  stats: {
    parts: Array<{
      id: string;
      name: string;
      icon: string;
      modules_count: number;
      skills_count: number;
      lessons_count: number;
    }>;
    total_modules: number;
    total_skills: number;
    total_lessons: number;
  };
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/stats/program`);
  if (!response.ok) {
    throw new Error('Failed to fetch stats');
  }
  return response.json();
}


// =============================================================================
// DATABASE PERSISTENCE API - Pour l'Application Mobile
// =============================================================================

export interface SaveLessonRequest {
  subject_name?: string;  // Nom du sujet (Français, Mathématiques, etc.)
  module_name: string;
  skill_name: string;
  title: string;
  subtitle?: string;
  lesson_number: number;
  rule: Record<string, unknown>;
  tips: Array<unknown>;
  examples: Array<Record<string, unknown>>;
  exercises: Array<Record<string, unknown>>;
}

/**
 * Vérifier si une leçon existe déjà en base.
 */
export async function checkLessonExists(lessonName: string): Promise<{
  success: boolean;
  exists: boolean;
  supabase_configured: boolean;
  lesson_id?: string;
  exercises_count?: number;
  message?: string;
}> {
  const response = await fetch(
    `${API_BASE_URL}/api/learning/db/check?lesson_name=${encodeURIComponent(lessonName)}`
  );
  if (!response.ok) {
    throw new Error('Failed to check lesson');
  }
  return response.json();
}

/**
 * Récupérer la liste de TOUTES les leçons existantes en base (optimisé).
 */
export async function getLessonsSummary(): Promise<{
  success: boolean;
  lessons: Record<string, number>;  // { "a_à": 6, "et_est": 10, ... }
  supabase_configured: boolean;
  total?: number;
  message?: string;
  error?: string;
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/db/lessons-summary`);
  if (!response.ok) {
    throw new Error('Failed to fetch lessons summary');
  }
  return response.json();
}

/**
 * Récupérer une leçon complète depuis la base de données.
 */
export async function getLessonFromDatabase(lessonName: string): Promise<{
  success: boolean;
  lesson?: Lesson;
  lesson_id?: string;
  exercises_count?: number;
  error?: string;
}> {
  const response = await fetch(
    `${API_BASE_URL}/api/learning/db/lesson?lesson_name=${encodeURIComponent(lessonName)}`
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch lesson');
  }
  return response.json();
}

/**
 * Sauvegarder une leçon générée en base.
 */
export async function saveLessonToDatabase(request: SaveLessonRequest): Promise<{
  success: boolean;
  lesson_id?: string;
  lesson_key?: string;
  exercises_count?: number;
  message: string;
  error?: string;
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/db/save-lesson`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to save lesson');
  }
  return response.json();
}

/**
 * Ajouter des exercices à une leçon existante.
 */
export async function addExercisesToLesson(
  lessonId: string,
  exercises: Array<Record<string, unknown>>
): Promise<{
  success: boolean;
  exercises_added?: number;
  total_exercises?: number;
  message: string;
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/db/add-exercises`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      lesson_id: lessonId,
      exercises: exercises,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to add exercises');
  }
  return response.json();
}

/**
 * Mettre à jour une leçon existante dans la base.
 */
export async function updateLesson(
  lessonName: string,
  lessonData: Partial<Lesson>
): Promise<{
  success: boolean;
  message: string;
  lesson_id?: string;
  exercises_count?: number;
}> {
  const response = await fetch(
    `${API_BASE_URL}/api/learning/db/lesson?lesson_name=${encodeURIComponent(lessonName)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(lessonData),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update lesson');
  }
  return response.json();
}

/**
 * Récupérer une leçon sauvegardée depuis la base.
 */
export async function getSavedLesson(
  lessonId: string,
  includeExercises: boolean = true
): Promise<{
  success: boolean;
  lesson?: Record<string, unknown>;
}> {
  const response = await fetch(
    `${API_BASE_URL}/api/learning/db/lesson/${encodeURIComponent(lessonId)}?include_exercises=${includeExercises}`
  );

  if (!response.ok) {
    if (response.status === 404) {
      return { success: false };
    }
    throw new Error('Failed to fetch saved lesson');
  }
  return response.json();
}

/**
 * Récupérer des exercices aléatoires d'une leçon.
 * POINT CLÉ: Permet de piocher N exercices parmi tous ceux stockés.
 */
export async function getRandomExercises(
  lessonId: string,
  count: number = 10,
  difficulty?: string,
  excludeIds?: string[]
): Promise<{
  success: boolean;
  count: number;
  exercises: Array<Record<string, unknown>>;
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/db/random-exercises`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      lesson_id: lessonId,
      count: count,
      difficulty: difficulty,
      exclude_ids: excludeIds,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get random exercises');
  }
  return response.json();
}

/**
 * Compter les exercices disponibles pour une leçon.
 */
export async function getExerciseCount(lessonId: string): Promise<{
  success: boolean;
  exercises_count: number;
}> {
  const response = await fetch(
    `${API_BASE_URL}/api/learning/db/exercise-count/${encodeURIComponent(lessonId)}`
  );
  if (!response.ok) {
    throw new Error('Failed to get exercise count');
  }
  return response.json();
}

/**
 * Statistiques du contenu stocké.
 */
export async function getStoredContentStats(): Promise<{
  success: boolean;
  stats: {
    total_lessons: number;
    total_exercises: number;
    by_part: Record<string, unknown>;
  };
}> {
  const response = await fetch(`${API_BASE_URL}/api/learning/db/stats`);
  if (!response.ok) {
    throw new Error('Failed to get stored content stats');
  }
  return response.json();
}

/**
 * Supprimer une leçon et tous ses exercices.
 */
export async function deleteLesson(lessonIdentifier: string): Promise<{
  success: boolean;
  message: string;
  exercises_deleted: number;
}> {
  const response = await fetch(
    `${API_BASE_URL}/api/learning/db/lesson?lesson_identifier=${encodeURIComponent(lessonIdentifier)}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete lesson');
  }
  return response.json();
}
